import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgClass, CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { ViewChild, Input, Directive, signal, computed, QueryList, forwardRef, booleanAttribute, ContentChildren, ContentChild, Optional, Self, ChangeDetectionStrategy, Component, HostListener, Inject, NgModule } from '@angular/core';
import { NxErrorComponent } from '@aposin/ng-aquila/base';
import { NxAbstractControl } from '@aposin/ng-aquila/shared';
import * as i2 from '@aposin/ng-aquila/utils';
import { removeClasses, mapClassNames } from '@aposin/ng-aquila/utils';
import { Subject, merge } from 'rxjs';
import { startWith, switchMap, takeUntil } from 'rxjs/operators';
import * as i1 from '@angular/forms';
import { FormsModule } from '@angular/forms';
import * as i3 from '@aposin/ng-aquila/icon';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import * as i1$1 from '@angular/cdk/collections';
import * as i2$1 from '@angular/cdk/a11y';
const _c0 = ["toggleButtonLabelContainer"];
const _c1 = ["toggleInput"];
const _c2 = ["*", [["nx-error"]]];
const _c3 = ["*", "nx-error"];
const _c4 = a0 => ({
  "disable-mobile": a0
});
function NxRadioToggleComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵprojection(1, 1);
    i0.ɵɵelementEnd();
  }
}
const _c5 = ["*"];
let nextId$1 = 0;
/** @docs-private */
class NxRadioToggleButtonBaseComponent {
  constructor(/** @docs-private */renderer) {
    this.renderer = renderer;
    /** @docs-private */
    this.toggleButtonId = 'nx-radio-toggle-button-' + nextId$1++;
  }
  /** @docs-private */
  triggerInputClick(event) {
    event.stopPropagation();
    this.toggleInput.nativeElement.click();
  }
  /** @docs-private */
  setFirstButton() {
    this.renderer.addClass(this.labelContainer.nativeElement, 'nx-radio-toggle__label-container--first');
  }
  /** @docs-private */
  setLastButton() {
    this.renderer.addClass(this.labelContainer.nativeElement, 'nx-radio-toggle__label-container--last');
  }
  /** @docs-private */
  resetClasses() {
    removeClasses(this.renderer, this.labelContainer, 'nx-radio-toggle__label-container--first nx-radio-toggle__label-container--last');
  }
  static {
    this.ɵfac = function NxRadioToggleButtonBaseComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxRadioToggleButtonBaseComponent)(i0.ɵɵdirectiveInject(i0.Renderer2));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxRadioToggleButtonBaseComponent,
      viewQuery: function NxRadioToggleButtonBaseComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 7);
          i0.ɵɵviewQuery(_c1, 7);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.labelContainer = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.toggleInput = _t.first);
        }
      },
      inputs: {
        value: "value"
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxRadioToggleButtonBaseComponent, [{
    type: Directive,
    args: [{
      standalone: true
    }]
  }], () => [{
    type: i0.Renderer2
  }], {
    value: [{
      type: Input
    }],
    labelContainer: [{
      type: ViewChild,
      args: ['toggleButtonLabelContainer', {
        static: true
      }]
    }],
    toggleInput: [{
      type: ViewChild,
      args: ['toggleInput', {
        static: true
      }]
    }]
  });
})();
let nextId = 0;
const MAPPING = {
  negative: 'nx-radio-toggle--negative',
  small: 'nx-radio-toggle--small'
};
const RESET_VALUES = [null, undefined, ''];
class NxRadioToggleComponent {
  /** Sets the component to the disabled state.*/
  set disabled(value) {
    const coerced = coerceBooleanProperty(value);
    if (this._disabled !== coerced) {
      this._disabled = coerced;
      this._disableChange.next();
    }
  }
  get disabled() {
    return this._disabled;
  }
  /** Sets the component to the readonly state.*/
  set readonly(value) {
    const coerced = coerceBooleanProperty(value);
    if (this._readonly !== coerced) {
      this._readonly = coerced;
      this._disableChange.next();
    }
  }
  get readonly() {
    return this._readonly;
  }
  /**
   * @deprecated use ariaLabel or ariaLabelledby instead
   * Sets the name used for accessibility.
   */
  set name(value) {
    if (this._ariaLabel !== value) {
      this._ariaLabel = value;
      this._cdr.markForCheck();
    }
  }
  get name() {
    return this._ariaLabel || '';
  }
  set ariaLabel(value) {
    this._ariaLabel = value;
    this._cdr.markForCheck();
  }
  get ariaLabel() {
    return this._ariaLabel;
  }
  set ariaLabelledBy(value) {
    this._ariaLabelledBy = value;
    this._cdr.markForCheck();
  }
  get ariaLabelledBy() {
    return this._ariaLabelledBy;
  }
  /** @docs-private */
  get id() {
    return `nx-radio-toggle-${this._toggleId}`;
  }
  /** Sets the modifiers for the component. */
  set style(value) {
    this.additionalClasses = mapClassNames(value, [], MAPPING);
  }
  /** @docs-private */
  get selection() {
    return this._selection;
  }
  constructor(_cdr, /** @docs-private */ngControl, _errorStateMatcher, _parentForm, _parentFormGroup) {
    this._cdr = _cdr;
    this.ngControl = ngControl;
    this._errorStateMatcher = _errorStateMatcher;
    this._parentForm = _parentForm;
    this._parentFormGroup = _parentFormGroup;
    this._toggleId = (nextId++).toString();
    /** Whether the component should switch to vertical buttons on mobile viewports. */
    this.disableMobile = false;
    /** @docs-private */
    this.errorState = signal(false);
    // emits to signal children to run change detection
    this._disableChange = new Subject();
    /** @docs-private */
    this.additionalClasses = '';
    this._disabled = false;
    this._readonly = false;
    this._ariaLabel = null;
    this._ariaLabelledBy = null;
    this.errorMessageId = computed(() => {
      const isErrorVisible = this.errorState() && this.errorChild;
      return isErrorVisible ? this.errorChild.id : null;
    });
    /** @docs-private */
    this.toggleButtons = new QueryList();
    this._destroyed = new Subject();
    this.onTouchedCallback = () => {};
    this.onChangeCallback = option => {};
    if (this.ngControl) {
      // Note: we provide the value accessor through here, instead of
      // the `providers` to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }
  ngAfterContentInit() {
    this.toggleButtons.changes.pipe(startWith(this.toggleButtons), switchMap(_ => merge(...this.toggleButtons.map(button => button.onChecked))), takeUntil(this._destroyed)).subscribe(change => {
      this._selection = change.value;
      this.change(this._selection);
    });
    this.toggleButtons.changes.pipe(startWith(this.toggleButtons), takeUntil(this._destroyed)).subscribe(toggles => {
      toggles.forEach(toggle => {
        toggle.resetClasses();
        if (toggle.value === this.selection) {
          // We need to defer the selection for the edge case that the button with the value of this.selection
          // didn't exist yet but was added afterwards to prevent changed after checked errors
          setTimeout(() => toggle.select());
        }
      });
      if (toggles.length > 0) {
        toggles.first.setFirstButton();
        toggles.last.setLastButton();
      }
    });
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
    this._disableChange.complete();
  }
  ngDoCheck() {
    if (this.ngControl) {
      // We need to re-evaluate this on every change detection cycle, because there are some
      // error triggers that we can't subscribe to (e.g. parent form submissions). This means
      // that whatever logic is in here has to be super lean or we risk destroying the performance.
      this.updateErrorState();
    }
  }
  registerOnChange(onChangeCallback) {
    this.onChangeCallback = onChangeCallback;
  }
  registerOnTouched(onTouchedCallback) {
    this.onTouchedCallback = onTouchedCallback;
  }
  /** Preselects the respective options. */
  writeValue(value) {
    this._selection = value;
    const correspondingButton = this.toggleButtons.find(button => button.value === this._selection);
    if (correspondingButton) {
      correspondingButton.select();
      return;
    }
    if (RESET_VALUES.includes(value)) {
      this.toggleButtons.map(button => button.deselect());
    }
  }
  setDisabledState(isDisabled) {
    this.disabled = isDisabled;
  }
  /** @docs-private */
  change(value) {
    this.onChangeCallback(value);
    if (this.onTouchedCallback) {
      this.onTouchedCallback();
    }
  }
  /** @docs-private */
  updateErrorState() {
    const parent = this._parentFormGroup || this._parentForm;
    const control = this.ngControl ? this.ngControl.control : null;
    const newState = this._errorStateMatcher.isErrorState(control, parent);
    this.errorState.set(newState);
  }
  setReadonly(value) {
    this.readonly = value;
    this._cdr.markForCheck();
  }
  static {
    this.ɵfac = function NxRadioToggleComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxRadioToggleComponent)(i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.NgControl, 10), i0.ɵɵdirectiveInject(i2.ErrorStateMatcher), i0.ɵɵdirectiveInject(i1.NgForm, 8), i0.ɵɵdirectiveInject(i1.FormGroupDirective, 8));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxRadioToggleComponent,
      selectors: [["nx-radio-toggle"]],
      contentQueries: function NxRadioToggleComponent_ContentQueries(rf, ctx, dirIndex) {
        if (rf & 1) {
          i0.ɵɵcontentQuery(dirIndex, NxErrorComponent, 5);
          i0.ɵɵcontentQuery(dirIndex, NxRadioToggleButtonBaseComponent, 4);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.errorChild = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.toggleButtons = _t);
        }
      },
      inputs: {
        disableMobile: [2, "disableMobile", "disableMobile", booleanAttribute],
        disabled: "disabled",
        readonly: [2, "readonly", "readonly", booleanAttribute],
        name: "name",
        ariaLabel: "ariaLabel",
        ariaLabelledBy: "ariaLabelledBy",
        style: [0, "variant", "style"],
        writeValue: [0, "selection", "writeValue"]
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioToggleComponent)
      }])],
      ngContentSelectors: _c3,
      decls: 3,
      vars: 10,
      consts: [["role", "radiogroup", 3, "ngClass"], [1, "error-container"]],
      template: function NxRadioToggleComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef(_c2);
          i0.ɵɵelementStart(0, "div", 0);
          i0.ɵɵprojection(1);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(2, NxRadioToggleComponent_Conditional_2_Template, 2, 0, "div", 1);
        }
        if (rf & 2) {
          i0.ɵɵclassMapInterpolate1("nx-radio-toggle ", ctx.additionalClasses, "");
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(8, _c4, ctx.disableMobile));
          i0.ɵɵattribute("aria-label", ctx.ariaLabel)("aria-labelledby", ctx.ariaLabelledBy)("aria-readonly", ctx.readonly);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.errorState() ? 2 : -1);
        }
      },
      dependencies: [NgClass],
      styles: ["[_nghost-%COMP%]{display:block}.nx-radio-toggle[_ngcontent-%COMP%]{display:flex;height:100%}@media (max-width: 703px){.nx-radio-toggle[_ngcontent-%COMP%]:not(.disable-mobile){flex-direction:column;text-align:center;width:100%;justify-content:center;align-items:center}}.error-container[_ngcontent-%COMP%]{margin-top:var(--radio-toggle-stack)}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxRadioToggleComponent, [{
    type: Component,
    args: [{
      selector: 'nx-radio-toggle',
      changeDetection: ChangeDetectionStrategy.OnPush,
      imports: [NgClass],
      providers: [{
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioToggleComponent)
      }],
      template: "<div\n    class=\"nx-radio-toggle {{ additionalClasses }}\"\n    [ngClass]=\"{ 'disable-mobile': disableMobile }\"\n    role=\"radiogroup\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-labelledby]=\"ariaLabelledBy\"\n    [attr.aria-readonly]=\"readonly\"\n>\n    <ng-content></ng-content>\n</div>\n@if (errorState()) {\n    <div class=\"error-container\">\n        <ng-content select=\"nx-error\"></ng-content>\n    </div>\n}\n",
      styles: [":host{display:block}.nx-radio-toggle{display:flex;height:100%}@media (max-width: 703px){.nx-radio-toggle:not(.disable-mobile){flex-direction:column;text-align:center;width:100%;justify-content:center;align-items:center}}.error-container{margin-top:var(--radio-toggle-stack)}\n"]
    }]
  }], () => [{
    type: i0.ChangeDetectorRef
  }, {
    type: i1.NgControl,
    decorators: [{
      type: Optional
    }, {
      type: Self
    }]
  }, {
    type: i2.ErrorStateMatcher
  }, {
    type: i1.NgForm,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i1.FormGroupDirective,
    decorators: [{
      type: Optional
    }]
  }], {
    errorChild: [{
      type: ContentChild,
      args: [NxErrorComponent]
    }],
    disableMobile: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    disabled: [{
      type: Input
    }],
    readonly: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    name: [{
      type: Input
    }],
    ariaLabel: [{
      type: Input
    }],
    ariaLabelledBy: [{
      type: Input
    }],
    toggleButtons: [{
      type: ContentChildren,
      args: [NxRadioToggleButtonBaseComponent]
    }],
    style: [{
      type: Input,
      args: ['variant']
    }],
    writeValue: [{
      type: Input,
      args: ['selection']
    }]
  });
})();

/** @docs-private */
class NxRadioToggleButtonChange {
  constructor(source, value) {
    this.source = source;
    this.value = value;
  }
}
class NxRadioToggleButtonComponent extends NxRadioToggleButtonBaseComponent {
  /** @docs-private */
  set disabled(value) {
    this._disabled = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get disabled() {
    return this._disabled || this.radioToggle?.disabled;
  }
  set readonly(value) {
    this._readonly = coerceBooleanProperty(value);
    this._cdr.markForCheck();
  }
  get readonly() {
    return this._readonly || this.radioToggle?.readonly;
  }
  /** Aria label for screen reader users */
  set ariaLabel(value) {
    this._ariaLabel = value;
  }
  get ariaLabel() {
    return this._ariaLabel;
  }
  /** Sets the checked state and notify siblings and the parent group about the change */
  // Only use this if you want the onChecked event to be fired, this will inform the parent about the change!
  // To select a button without firing the event use the select() function
  set selected(value) {
    if (this._selected !== value) {
      this._selected = value;
      if (this._selected) {
        this._notifySiblings();
        this._emitCheckedEvent();
      }
      this._cdr.markForCheck();
    }
  }
  get selected() {
    return this._selected;
  }
  constructor(radioToggle, /** @docs-private */renderer, _cdr, _toggleDispatcher, _focusMonitor) {
    super(renderer);
    this.radioToggle = radioToggle;
    this._cdr = _cdr;
    this._toggleDispatcher = _toggleDispatcher;
    this._focusMonitor = _focusMonitor;
    /** @docs-private */
    // emits when the button is checked to notify the group
    this.onChecked = new Subject();
    this.disableMobile = false;
    this._disabled = false;
    this._readonly = false;
    this._ariaLabel = null;
    /** @docs-private */
    this.controlInvalid = computed(() => this.radioToggle?.errorState() || null);
    this._selected = false;
    this._destroyed = new Subject();
    /** Unregister function for _expansionDispatcher. */
    this._removeUniqueSelectionListener = () => {};
    this.errorMessageId = computed(() => this.radioToggle?.errorMessageId() || null);
    this._removeUniqueSelectionListener = this._toggleDispatcher.listen((id, radioToggleId) => {
      if (this.radioToggle && this.radioToggle.id === radioToggleId && this.id !== id) {
        this._selected = false;
        // need to let change detector know in case that the select() function was called on another button
        this._cdr.markForCheck();
      }
    });
    this.radioToggle._disableChange.pipe(takeUntil(this._destroyed)).subscribe(() => {
      this._cdr.markForCheck();
    });
  }
  ngAfterViewInit() {
    this._focusMonitor.monitor(this.toggleInput);
  }
  ngOnDestroy() {
    this._removeUniqueSelectionListener();
    this._destroyed.next();
    this._destroyed.complete();
    this._focusMonitor.stopMonitoring(this.toggleInput);
  }
  setReadonly(value) {
    this.readonly = value;
    this._cdr.markForCheck();
  }
  /** @docs-private */
  get toggleId() {
    return this.radioToggle.id;
  }
  /** @docs-private */
  get id() {
    return this.toggleButtonId;
  }
  _notifySiblings() {
    const toggleId = this.radioToggle ? this.radioToggle.id : this.id;
    this._toggleDispatcher.notify(this.id, toggleId);
  }
  _emitCheckedEvent() {
    this.onChecked.next(new NxRadioToggleButtonChange(this, this.value));
  }
  // Does NOT emit the onChecked event. Useful when you need to set initial
  // state. Used by the parent to set checked states on initialization and during
  // writeValue() changes.
  /** @docs-private */
  select() {
    if (!this._selected) {
      this._selected = true;
      this._notifySiblings();
    }
    this._cdr.markForCheck();
  }
  /** @docs-private */
  deselect() {
    if (this._selected) {
      this._selected = false;
    }
    this._cdr.markForCheck();
  }
  /** @docs-private */
  onKeyupSpace() {
    this._selected = false;
    this._notifySiblings();
  }
  /** Forward focus from host to hidden input field */
  _forwardFocusToInput() {
    this.toggleInput.nativeElement.focus();
  }
  _onInputClick(event) {
    if (this.readonly) {
      event.preventDefault();
      return;
    }
    this.selected = true;
  }
  static {
    this.ɵfac = function NxRadioToggleButtonComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxRadioToggleButtonComponent)(i0.ɵɵdirectiveInject(forwardRef(() => NxRadioToggleComponent)), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1$1.UniqueSelectionDispatcher), i0.ɵɵdirectiveInject(i2$1.FocusMonitor));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxRadioToggleButtonComponent,
      selectors: [["nx-radio-toggle-button"]],
      hostVars: 4,
      hostBindings: function NxRadioToggleButtonComponent_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("focus", function NxRadioToggleButtonComponent_focus_HostBindingHandler() {
            return ctx._forwardFocusToInput();
          })("keyup.space", function NxRadioToggleButtonComponent_keyup_space_HostBindingHandler() {
            return ctx.onKeyupSpace();
          });
        }
        if (rf & 2) {
          i0.ɵɵclassProp("has-error", ctx.controlInvalid())("is-readonly", ctx.readonly);
        }
      },
      inputs: {
        disableMobile: [2, "disableMobile", "disableMobile", booleanAttribute],
        disabled: "disabled",
        readonly: [2, "readonly", "readonly", booleanAttribute],
        ariaLabel: "ariaLabel",
        selected: "selected"
      },
      features: [i0.ɵɵProvidersFeature([{
        provide: NxRadioToggleButtonBaseComponent,
        useExisting: forwardRef(() => NxRadioToggleButtonComponent)
      }, {
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioToggleButtonComponent)
      }]), i0.ɵɵInheritDefinitionFeature],
      ngContentSelectors: _c5,
      decls: 7,
      vars: 20,
      consts: [["toggleInput", ""], ["toggleButtonLabelContainer", ""], ["type", "radio", 1, "nx-radio-toggle__input", 3, "click", "checked", "disabled", "ngClass"], [1, "nx-radio-toggle__label-container", 3, "click", "ngClass"], [1, "nx-radio-toggle__label", 3, "ngClass"], ["name", "check", "aria-hidden", "true", 1, "nx-toggle-button__mobile-check"]],
      template: function NxRadioToggleButtonComponent_Template(rf, ctx) {
        if (rf & 1) {
          const _r1 = i0.ɵɵgetCurrentView();
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "input", 2, 0);
          i0.ɵɵlistener("click", function NxRadioToggleButtonComponent_Template_input_click_0_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx._onInputClick($event));
          });
          i0.ɵɵelementEnd();
          i0.ɵɵelementStart(2, "div", 3, 1);
          i0.ɵɵlistener("click", function NxRadioToggleButtonComponent_Template_div_click_2_listener($event) {
            i0.ɵɵrestoreView(_r1);
            return i0.ɵɵresetView(ctx.triggerInputClick($event));
          });
          i0.ɵɵelementStart(4, "label", 4);
          i0.ɵɵelement(5, "nx-icon", 5);
          i0.ɵɵprojection(6);
          i0.ɵɵelementEnd()();
        }
        if (rf & 2) {
          i0.ɵɵclassProp("is-readonly", ctx.readonly);
          i0.ɵɵproperty("checked", ctx.selected)("disabled", ctx.disabled)("ngClass", i0.ɵɵpureFunction1(14, _c4, ctx.disableMobile));
          i0.ɵɵattribute("id", ctx.toggleButtonId)("name", ctx.toggleId)("aria-label", ctx.ariaLabel)("aria-disabled", ctx.readonly || null)("aria-describedby", ctx.errorMessageId())("aria-invalid", ctx.controlInvalid());
          i0.ɵɵadvance(2);
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(16, _c4, ctx.disableMobile));
          i0.ɵɵadvance(2);
          i0.ɵɵproperty("ngClass", i0.ɵɵpureFunction1(18, _c4, ctx.disableMobile));
          i0.ɵɵattribute("for", ctx.toggleButtonId);
        }
      },
      dependencies: [NxIconModule, i3.NxIconComponent, NgClass],
      styles: ["[_nghost-%COMP%]{display:flex;position:relative;flex-direction:row}@media (max-width: 703px){[_nghost-%COMP%]{width:100%}}.has-error[_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{border-color:var(--danger);background-color:var(--danger)}.has-error[_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]{border-color:var(--danger);color:var(--danger)}.has-error[_nghost-%COMP%]:hover   .nx-radio-toggle__label-container[_ngcontent-%COMP%]{color:var(--toggle-button-hover-text-color)}.nx-radio-toggle__input[_ngcontent-%COMP%]{opacity:0;position:absolute}.nx-radio-toggle__input[_ngcontent-%COMP%]:active + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--toggle-button-active-background-color);border-color:var(--toggle-button-active-border-color);color:var(--toggle-button-active-text-color)}.nx-radio-toggle__input[_ngcontent-%COMP%]:checked:disabled + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--toggle-button-disabled-selected-background-color);border-color:var(--toggle-button-disabled-selected-border-color);color:var(--toggle-button-disabled-selected-text-color)}.nx-radio-toggle__input[_ngcontent-%COMP%]:disabled + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--toggle-button-disabled-background-color);border-color:var(--toggle-button-disabled-border-color);color:var(--toggle-button-disabled-text-color);cursor:not-allowed}.nx-radio-toggle__input[_ngcontent-%COMP%]:disabled + .nx-radio-toggle__label-container[_ngcontent-%COMP%]   .nx-radio-toggle__label[_ngcontent-%COMP%]{cursor:not-allowed}.nx-radio-toggle__input.is-readonly[_ngcontent-%COMP%] + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--toggle-button-readonly-unselected-background-color);border-color:var(--toggle-button-readonly-unselected-border-color);color:var(--toggle-button-readonly-unselected-text-color)}.nx-radio-toggle__input.is-readonly[_ngcontent-%COMP%] + .nx-radio-toggle__label-container[_ngcontent-%COMP%]   .nx-radio-toggle__label[_ngcontent-%COMP%]:hover{cursor:not-allowed}.nx-radio-toggle__input.is-readonly[_ngcontent-%COMP%] + .nx-radio-toggle__label-container[_ngcontent-%COMP%]:hover{cursor:not-allowed}.nx-radio-toggle__input[_ngcontent-%COMP%]:checked.is-readonly + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--toggle-button-readonly-selected-background-color);border-color:var(--toggle-button-readonly-selected-border-color);color:var(--toggle-button-readonly-selected-text-color)}.nx-radio-toggle__input[_ngcontent-%COMP%]:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--toggle-button-selected-background-color);border-color:var(--toggle-button-selected-border-color);color:var(--toggle-button-selected-text-color);cursor:default}.nx-radio-toggle__input[_ngcontent-%COMP%]:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%]   .nx-radio-toggle__label[_ngcontent-%COMP%]{cursor:default}.nx-toggle-button__mobile-check[_ngcontent-%COMP%]{display:none;font-size:24px;margin-right:8px;height:24px}[dir=rtl][_nghost-%COMP%]   .nx-toggle-button__mobile-check[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-toggle-button__mobile-check[_ngcontent-%COMP%]{margin-right:initial;margin-left:8px}.nx-radio-toggle--small[_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%], .nx-radio-toggle--small   [_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]{font-size:var(--toggle-button-small-font-size);line-height:var(--toggle-button-small-line-height);font-weight:var(--toggle-button-small-font-weight);letter-spacing:var(--toggle-button-small-letter-spacing);min-height:16px;padding:6px;text-transform:none}.nx-radio-toggle[_ngcontent-%COMP%]{-webkit-user-select:none;user-select:none;cursor:pointer}@media (max-width: 703px){  .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container{border-width:1px 2px;padding:11px 31px}  .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first{border-width:2px;border-bottom-width:1px;border-radius:4px 4px 0 0}  .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last{border-width:2px;border-top-width:1px;border-radius:0 0 4px 4px}  .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last.nx-radio-toggle__label-container--first{border-radius:4px;border-width:2px}}@media (max-width: 703px){  .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__input:checked+.nx-radio-toggle__label-container .nx-toggle-button__mobile-check{display:inline-block}}@media (max-width: 703px){  .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle--small-shadowcsshost-no-combinator .nx-radio-toggle__label-container, .nx-radio-toggle--small   [_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]{font-size:var(--toggle-button-large-font-size);line-height:var(--toggle-button-large-line-height);font-weight:var(--toggle-button-large-font-weight);letter-spacing:var(--toggle-button-large-letter-spacing);padding:11px 31px}}.nx-radio-toggle__label-container.nx-radio-toggle__label-container--first[_ngcontent-%COMP%]{border-radius:4px 0 0 4px;border-width:2px 0 2px 2px}[dir=rtl][_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first[_ngcontent-%COMP%]{border-radius:0 4px 4px 0;border-width:2px 2px 2px 0}@media (max-width: 703px){[dir=rtl][_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first[_ngcontent-%COMP%]{border-width:2px;border-bottom-width:1px;border-radius:4px 4px 0 0}}.nx-radio-toggle__label-container.nx-radio-toggle__label-container--last[_ngcontent-%COMP%]{border-radius:0 4px 4px 0;border-width:2px}[dir=rtl][_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last[_ngcontent-%COMP%]{border-radius:4px 0 0 4px;border-width:2px}@media (max-width: 703px){[dir=rtl][_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last[_ngcontent-%COMP%]{border-width:2px;border-top-width:1px;border-radius:0 0 4px 4px}}.nx-radio-toggle__label-container[_ngcontent-%COMP%]{font-size:var(--toggle-button-large-font-size);line-height:var(--toggle-button-large-line-height);font-weight:var(--toggle-button-large-font-weight);letter-spacing:var(--toggle-button-large-letter-spacing);padding:10px 30px;color:var(--toggle-button-unselected-text-color);background-color:var(--toggle-button-unselected-background-color);border-color:var(--toggle-button-unselected-border-color);border-style:solid;border-radius:0;border-width:2px 0 2px 2px;cursor:pointer;flex-grow:1;text-align:center;text-transform:var(--button-text-transform);transition:background-color .2s ease,border-color .2s ease,color .2s ease;display:flex;align-items:center;height:100%;min-height:24px}@media (max-width: 703px){.nx-radio-toggle__label-container[_ngcontent-%COMP%]{justify-content:center}}[dir=rtl][_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]{border-width:2px 2px 2px 0}@media (max-width: 703px){[dir=rtl][_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]{border-width:1px 2px}}.nx-radio-toggle__label-container[_ngcontent-%COMP%]:hover{background-color:var(--toggle-button-hover-background-color);border-color:var(--toggle-button-hover-border-color);color:var(--toggle-button-hover-text-color)}.nx-radio-toggle--negative[_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:focus + .nx-radio-toggle__label-container[_ngcontent-%COMP%], .nx-radio-toggle--negative   [_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:focus + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--negative-01);border-color:var(--negative-01);color:var(--negative-accent)}.nx-radio-toggle--negative[_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:active + .nx-radio-toggle__label-container[_ngcontent-%COMP%], .nx-radio-toggle--negative   [_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:active + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--negative-02);border-color:var(--negative-02);color:var(--negative-accent)}.nx-radio-toggle--negative[_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:disabled:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%], .nx-radio-toggle--negative   [_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:disabled:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--negative-dimmed);border-color:transparent;color:var(--negative-background-dimmed)}.nx-radio-toggle--negative[_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:disabled + .nx-radio-toggle__label-container[_ngcontent-%COMP%], .nx-radio-toggle--negative   [_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:disabled + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:transparent;border-color:var(--negative-dimmed);color:var(--negative-dimmed)}.nx-radio-toggle--negative[_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%], .nx-radio-toggle--negative   [_nghost-%COMP%]   .nx-radio-toggle__input[_ngcontent-%COMP%]:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:var(--negative);border-color:var(--negative);color:var(--negative-accent)}.nx-radio-toggle--negative[_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%], .nx-radio-toggle--negative   [_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]{border-color:var(--negative);color:var(--negative)}.nx-radio-toggle--negative[_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]:hover, .nx-radio-toggle--negative   [_nghost-%COMP%]   .nx-radio-toggle__label-container[_ngcontent-%COMP%]:hover{background-color:var(--negative-01);border-color:var(--negative-01);color:var(--negative-accent)}.nx-radio-toggle__label[_ngcontent-%COMP%]{display:flex;align-items:center;cursor:pointer}.nx-radio-toggle__input.cdk-keyboard-focused[_ngcontent-%COMP%] + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{box-shadow:var(--focus-box-shadow);z-index:1}@media screen and (forced-colors: active),(forced-colors: active){.nx-radio-toggle__input.cdk-keyboard-focused[_ngcontent-%COMP%] + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}@media screen and (forced-colors: active){[_nghost-%COMP%]{forced-color-adjust:none}.nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:buttonFace!important;border-color:buttonText!important;color:buttonText!important}.nx-radio-toggle__label-container[_ngcontent-%COMP%]   .nx-radio-toggle__label[_ngcontent-%COMP%]{forced-colors-adjust:none}.nx-radio-toggle__input[_ngcontent-%COMP%]:disabled + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:buttonFace!important;border-color:GrayText!important;color:GrayText!important}.nx-radio-toggle__input[_ngcontent-%COMP%]:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%]{background-color:highlight!important;border-color:buttonText!important}.nx-radio-toggle__input[_ngcontent-%COMP%]:checked + .nx-radio-toggle__label-container[_ngcontent-%COMP%]   .nx-radio-toggle__label[_ngcontent-%COMP%]{color:highlightText!important}}"],
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxRadioToggleButtonComponent, [{
    type: Component,
    args: [{
      selector: 'nx-radio-toggle-button',
      changeDetection: ChangeDetectionStrategy.OnPush,
      providers: [{
        provide: NxRadioToggleButtonBaseComponent,
        useExisting: forwardRef(() => NxRadioToggleButtonComponent)
      }, {
        provide: NxAbstractControl,
        useExisting: forwardRef(() => NxRadioToggleButtonComponent)
      }],
      host: {
        '[class.has-error]': 'controlInvalid()',
        '(focus)': '_forwardFocusToInput()',
        '[class.is-readonly]': 'readonly'
      },
      imports: [NxIconModule, NgClass],
      template: "<input\n    [attr.id]=\"toggleButtonId\"\n    #toggleInput\n    [attr.name]=\"toggleId\"\n    [checked]=\"selected\"\n    class=\"nx-radio-toggle__input\"\n    type=\"radio\"\n    (click)=\"_onInputClick($event)\"\n    [disabled]=\"disabled\"\n    [ngClass]=\"{ 'disable-mobile': disableMobile }\"\n    [attr.aria-label]=\"ariaLabel\"\n    [attr.aria-disabled]=\"readonly || null\"\n    [attr.aria-describedby]=\"errorMessageId()\"\n    [attr.aria-invalid]=\"controlInvalid()\"\n    [class.is-readonly]=\"readonly\"\n/>\n<div (click)=\"triggerInputClick($event)\" [ngClass]=\"{ 'disable-mobile': disableMobile }\" #toggleButtonLabelContainer class=\"nx-radio-toggle__label-container\">\n    <label [attr.for]=\"toggleButtonId\" class=\"nx-radio-toggle__label\" [ngClass]=\"{ 'disable-mobile': disableMobile }\">\n        <nx-icon class=\"nx-toggle-button__mobile-check\" name=\"check\" aria-hidden=\"true\"></nx-icon>\n        <ng-content></ng-content>\n    </label>\n</div>\n",
      styles: [":host{display:flex;position:relative;flex-direction:row}@media (max-width: 703px){:host{width:100%}}:host.has-error .nx-radio-toggle__input:checked+.nx-radio-toggle__label-container{border-color:var(--danger);background-color:var(--danger)}:host.has-error .nx-radio-toggle__label-container{border-color:var(--danger);color:var(--danger)}:host.has-error:hover .nx-radio-toggle__label-container{color:var(--toggle-button-hover-text-color)}.nx-radio-toggle__input{opacity:0;position:absolute}.nx-radio-toggle__input:active+.nx-radio-toggle__label-container{background-color:var(--toggle-button-active-background-color);border-color:var(--toggle-button-active-border-color);color:var(--toggle-button-active-text-color)}.nx-radio-toggle__input:checked:disabled+.nx-radio-toggle__label-container{background-color:var(--toggle-button-disabled-selected-background-color);border-color:var(--toggle-button-disabled-selected-border-color);color:var(--toggle-button-disabled-selected-text-color)}.nx-radio-toggle__input:disabled+.nx-radio-toggle__label-container{background-color:var(--toggle-button-disabled-background-color);border-color:var(--toggle-button-disabled-border-color);color:var(--toggle-button-disabled-text-color);cursor:not-allowed}.nx-radio-toggle__input:disabled+.nx-radio-toggle__label-container .nx-radio-toggle__label{cursor:not-allowed}.nx-radio-toggle__input.is-readonly+.nx-radio-toggle__label-container{background-color:var(--toggle-button-readonly-unselected-background-color);border-color:var(--toggle-button-readonly-unselected-border-color);color:var(--toggle-button-readonly-unselected-text-color)}.nx-radio-toggle__input.is-readonly+.nx-radio-toggle__label-container .nx-radio-toggle__label:hover{cursor:not-allowed}.nx-radio-toggle__input.is-readonly+.nx-radio-toggle__label-container:hover{cursor:not-allowed}.nx-radio-toggle__input:checked.is-readonly+.nx-radio-toggle__label-container{background-color:var(--toggle-button-readonly-selected-background-color);border-color:var(--toggle-button-readonly-selected-border-color);color:var(--toggle-button-readonly-selected-text-color)}.nx-radio-toggle__input:checked+.nx-radio-toggle__label-container{background-color:var(--toggle-button-selected-background-color);border-color:var(--toggle-button-selected-border-color);color:var(--toggle-button-selected-text-color);cursor:default}.nx-radio-toggle__input:checked+.nx-radio-toggle__label-container .nx-radio-toggle__label{cursor:default}.nx-toggle-button__mobile-check{display:none;font-size:24px;margin-right:8px;height:24px}:host-context([dir=rtl]) .nx-toggle-button__mobile-check{margin-right:initial;margin-left:8px}:host-context(.nx-radio-toggle--small) .nx-radio-toggle__label-container{font-size:var(--toggle-button-small-font-size);line-height:var(--toggle-button-small-line-height);font-weight:var(--toggle-button-small-font-weight);letter-spacing:var(--toggle-button-small-letter-spacing);min-height:16px;padding:6px;text-transform:none}.nx-radio-toggle{-webkit-user-select:none;user-select:none;cursor:pointer}@media (max-width: 703px){::ng-deep .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container{border-width:1px 2px;padding:11px 31px}::ng-deep .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first{border-width:2px;border-bottom-width:1px;border-radius:4px 4px 0 0}::ng-deep .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last{border-width:2px;border-top-width:1px;border-radius:0 0 4px 4px}::ng-deep .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last.nx-radio-toggle__label-container--first{border-radius:4px;border-width:2px}}@media (max-width: 703px){::ng-deep .nx-radio-toggle:not(.disable-mobile) .nx-radio-toggle__input:checked+.nx-radio-toggle__label-container .nx-toggle-button__mobile-check{display:inline-block}}@media (max-width: 703px){::ng-deep .nx-radio-toggle:not(.disable-mobile) :host-context(.nx-radio-toggle--small) .nx-radio-toggle__label-container{font-size:var(--toggle-button-large-font-size);line-height:var(--toggle-button-large-line-height);font-weight:var(--toggle-button-large-font-weight);letter-spacing:var(--toggle-button-large-letter-spacing);padding:11px 31px}}.nx-radio-toggle__label-container.nx-radio-toggle__label-container--first{border-radius:4px 0 0 4px;border-width:2px 0 2px 2px}:host-context([dir=rtl]) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first{border-radius:0 4px 4px 0;border-width:2px 2px 2px 0}@media (max-width: 703px){:host-context([dir=rtl]) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--first{border-width:2px;border-bottom-width:1px;border-radius:4px 4px 0 0}}.nx-radio-toggle__label-container.nx-radio-toggle__label-container--last{border-radius:0 4px 4px 0;border-width:2px}:host-context([dir=rtl]) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last{border-radius:4px 0 0 4px;border-width:2px}@media (max-width: 703px){:host-context([dir=rtl]) .nx-radio-toggle__label-container.nx-radio-toggle__label-container--last{border-width:2px;border-top-width:1px;border-radius:0 0 4px 4px}}.nx-radio-toggle__label-container{font-size:var(--toggle-button-large-font-size);line-height:var(--toggle-button-large-line-height);font-weight:var(--toggle-button-large-font-weight);letter-spacing:var(--toggle-button-large-letter-spacing);padding:10px 30px;color:var(--toggle-button-unselected-text-color);background-color:var(--toggle-button-unselected-background-color);border-color:var(--toggle-button-unselected-border-color);border-style:solid;border-radius:0;border-width:2px 0 2px 2px;cursor:pointer;flex-grow:1;text-align:center;text-transform:var(--button-text-transform);transition:background-color .2s ease,border-color .2s ease,color .2s ease;display:flex;align-items:center;height:100%;min-height:24px}@media (max-width: 703px){.nx-radio-toggle__label-container{justify-content:center}}:host-context([dir=rtl]) .nx-radio-toggle__label-container{border-width:2px 2px 2px 0}@media (max-width: 703px){:host-context([dir=rtl]) .nx-radio-toggle__label-container{border-width:1px 2px}}.nx-radio-toggle__label-container:hover{background-color:var(--toggle-button-hover-background-color);border-color:var(--toggle-button-hover-border-color);color:var(--toggle-button-hover-text-color)}:host-context(.nx-radio-toggle--negative) .nx-radio-toggle__input:focus+.nx-radio-toggle__label-container{background-color:var(--negative-01);border-color:var(--negative-01);color:var(--negative-accent)}:host-context(.nx-radio-toggle--negative) .nx-radio-toggle__input:active+.nx-radio-toggle__label-container{background-color:var(--negative-02);border-color:var(--negative-02);color:var(--negative-accent)}:host-context(.nx-radio-toggle--negative) .nx-radio-toggle__input:disabled:checked+.nx-radio-toggle__label-container{background-color:var(--negative-dimmed);border-color:transparent;color:var(--negative-background-dimmed)}:host-context(.nx-radio-toggle--negative) .nx-radio-toggle__input:disabled+.nx-radio-toggle__label-container{background-color:transparent;border-color:var(--negative-dimmed);color:var(--negative-dimmed)}:host-context(.nx-radio-toggle--negative) .nx-radio-toggle__input:checked+.nx-radio-toggle__label-container{background-color:var(--negative);border-color:var(--negative);color:var(--negative-accent)}:host-context(.nx-radio-toggle--negative) .nx-radio-toggle__label-container{border-color:var(--negative);color:var(--negative)}:host-context(.nx-radio-toggle--negative) .nx-radio-toggle__label-container:hover{background-color:var(--negative-01);border-color:var(--negative-01);color:var(--negative-accent)}.nx-radio-toggle__label{display:flex;align-items:center;cursor:pointer}.nx-radio-toggle__input.cdk-keyboard-focused+.nx-radio-toggle__label-container{box-shadow:var(--focus-box-shadow);z-index:1}@media screen and (forced-colors: active),(forced-colors: active){.nx-radio-toggle__input.cdk-keyboard-focused+.nx-radio-toggle__label-container{box-shadow:0 0 0 2px background,0 0 0 6px CanvasText;outline:4px solid CanvasText;outline-offset:2px}}@media screen and (forced-colors: active){:host{forced-color-adjust:none}.nx-radio-toggle__label-container{background-color:buttonFace!important;border-color:buttonText!important;color:buttonText!important}.nx-radio-toggle__label-container .nx-radio-toggle__label{forced-colors-adjust:none}.nx-radio-toggle__input:disabled+.nx-radio-toggle__label-container{background-color:buttonFace!important;border-color:GrayText!important;color:GrayText!important}.nx-radio-toggle__input:checked+.nx-radio-toggle__label-container{background-color:highlight!important;border-color:buttonText!important}.nx-radio-toggle__input:checked+.nx-radio-toggle__label-container .nx-radio-toggle__label{color:highlightText!important}}\n"]
    }]
  }], () => [{
    type: NxRadioToggleComponent,
    decorators: [{
      type: Inject,
      args: [forwardRef(() => NxRadioToggleComponent)]
    }]
  }, {
    type: i0.Renderer2
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i1$1.UniqueSelectionDispatcher
  }, {
    type: i2$1.FocusMonitor
  }], {
    disableMobile: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    disabled: [{
      type: Input
    }],
    readonly: [{
      type: Input,
      args: [{
        transform: booleanAttribute
      }]
    }],
    ariaLabel: [{
      type: Input
    }],
    selected: [{
      type: Input
    }],
    onKeyupSpace: [{
      type: HostListener,
      args: ['keyup.space']
    }]
  });
})();
class NxRadioToggleModule {
  static {
    this.ɵfac = function NxRadioToggleModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxRadioToggleModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxRadioToggleModule,
      imports: [CommonModule, FormsModule, NxIconModule, NxRadioToggleComponent, NxRadioToggleButtonComponent],
      exports: [NxRadioToggleComponent, NxRadioToggleButtonComponent]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      imports: [CommonModule, FormsModule, NxIconModule, NxRadioToggleButtonComponent]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxRadioToggleModule, [{
    type: NgModule,
    args: [{
      exports: [NxRadioToggleComponent, NxRadioToggleButtonComponent],
      imports: [CommonModule, FormsModule, NxIconModule, NxRadioToggleComponent, NxRadioToggleButtonComponent]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NxRadioToggleButtonBaseComponent, NxRadioToggleButtonChange, NxRadioToggleButtonComponent, NxRadioToggleComponent, NxRadioToggleModule, RESET_VALUES };
